// Filter parameters to query string for endpoints
type QueryParams = Record<string, string | number | boolean>;

// Base URL for reporting API endpoints (from environment variables)
const REPORTING_API_BASE_URL = process.env.REACT_APP_REPORTING_API_BASE_URL;
const DASH = "dashboard"

const filterParamsToQuery = (endpoint: string, params?: QueryParams) => {
  if (!params) return endpoint;
  const query = Object.entries(params)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");
  return query ? endpoint + "?" + query : endpoint;
};

const endpoints = {
  auth: {
    login: "/auth/login",
    logout: "/auth/logout",
    signup: "/auth/signup",
    generateOtp: "/auth/generate-otp",
    verifyOtp: "/auth/verify-otp",
    resetPassword: "/auth/reset-password-with-otp",
    forgotPassword: "/auth/forgot-password",
    getCurrentUser: "/auth/me",
    refreshToken: "/auth/refresh-token",
  },
  dashboard: {
    summary: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/summary`, params),
    countryWiseActiveUsers: `/${DASH}/users-by-country`,
    fetchExposerData: (params?: QueryParams) =>
      filterParamsToQuery(
        `${REPORTING_API_BASE_URL}/compare_exposure_data`, // TODO: Base URL to be changed
        params
      ),
    allSurveys: `/${DASH}/surveys/all`,
    surveyQuestions: (surveyId: string | number) => `/${DASH}/surveys/${surveyId}/questions`,
    surveyAnswers: (surveyId: string | number, questionId: string | number) => 
      `/${DASH}/surveys/${surveyId}/questions/${questionId}/answers`,
    surveyInsights: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/surveys/survey-insights`, params),
    brandInsights: () => `${REPORTING_API_BASE_URL}/surveys/brand-insights`,
    shopLift: () => `${REPORTING_API_BASE_URL}/campaigns/shop-lift`,
    shopEffect: () => `${REPORTING_API_BASE_URL}/receipts/shop-effect`,
    receiptAnalysis: () => `${REPORTING_API_BASE_URL}/receipts/receipt-analysis`,
    ageGroupAnalysis: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign-analysis/age-group`, params),
    weeklyReachAnalysis: () => `${REPORTING_API_BASE_URL}/campaign-analysis/weekly-reach`,
    genderAnalysis: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign-analysis/gender-data`, params),
    contactFrequencyAnalysis: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign-analysis/contact-frequency`, params),
  },
  locationTracking: {
    list: "/location-tracking",
    details: (id: string) => `/location-tracking/${id}`,
  },
  rewards: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/rewards`, params),
    create: `/${DASH}/rewards`,
    delete: (rewardId: string | number) => `/${DASH}/rewards/${rewardId}`,
    details: (rewardId: string | number) => `/${DASH}/rewards/${rewardId}`,
    update: (rewardId: string | number) => `/${DASH}/rewards/${rewardId}`,
    // Rankings endpoints
    currentRanking: (category: string) => `/${DASH}/rankings-and-history/current-ranking/${category}`,
    pointHistory: (params?: QueryParams) => 
      filterParamsToQuery(`/${DASH}/rankings-and-history/point-history`, params),
    pointHistoryInsights: (userId: string | number, params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/rankings-and-history/point-history/${userId}`, params),
  },
  smartReceipt: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/smart-receipts`, params),
    create: `/${DASH}/smart-receipts`,
    details: (id: string | number) => `/${DASH}/smart-receipts/${id}`,
    update: (id: string | number) => `/${DASH}/smart-receipts/${id}`,
    delete: (id: string | number) => `/${DASH}/smart-receipts/${id}`,
    uploadDetails: `/${DASH}/smart-receipts/receipt-upload-details`,
  },
  giftShop: {
    vendorProducts: (params?: QueryParams) =>
      filterParamsToQuery(
        `/${DASH}/gift-cards/get-available-products`,
        params
      ),
    vendorProductDetails: (productId: string, params: QueryParams) =>
      filterParamsToQuery(
        `/${DASH}/gift-cards/get-product-by-id/${productId}`,
        params
      ),
    create: `/${DASH}/gift-cards`,
    update: (params: QueryParams) =>
      filterParamsToQuery(`/${DASH}/gift-cards`, params),
    giftCardList: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/gift-cards`, params),
    giftCardStatusUpdate: (id: string | number, params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/gift-cards/${id}`, params),
    giftCardDetails: (id: string | number) => `/${DASH}/gift-cards/${id}`,
    orderHistoryList: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/gift-cards/orders`, params),
  },
  reporting: {
    // Old endpoints (kept for backward compatibility)
    locationMatching: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/reports/location-match`, params),
    locationCampaignInsights: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/reports/location-match/users`, params),
    audioMatching: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/reports/audio-match`, params),
    audioCampaignInsights: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/reports/audio-match/users`, params),
    
    // New reporting API endpoints
    locationMatchingReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign_location_proximity`, params),
    locationCampaignInsightsReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign-location-proximity/users`, params),
    audioMatchingReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign_audio_proximity`, params),
    audioCampaignInsightsReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/campaign_audio_proximity/users`, params),
    storeMatchingReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/store_visits`, params),
    storeCampaignInsightsReport: (params?: QueryParams) =>
      filterParamsToQuery(`${REPORTING_API_BASE_URL}/store-visits/users`, params),
  },
  clients: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/clients`, params),
    create: `/${DASH}/clients`,
    update: (clientId: string | number) => `/${DASH}/clients/${clientId}`,
    updateLocations: (clientId: string | number) =>
      `/${DASH}/clients/${clientId}/locations`,
    details: (id: string) => `/${DASH}/clients/${id}`,
    all: `/${DASH}/clients/all`,
    mediaList: (clientId: string | number) => `${REPORTING_API_BASE_URL}/clients/${clientId}/media-list`,
    smartRequestList: (clientId: string | number) => `${REPORTING_API_BASE_URL}/clients/${clientId}/smart-request-list`,
    visitedShopList: (clientId: string | number) => `${REPORTING_API_BASE_URL}/clients/${clientId}/visited-shop-list`,
  },
  users: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/mobile-users`, params),
    create: `/${DASH}/mobile-users`,
    update: (userId: string | number) => `/${DASH}/mobile-users/${userId}`,
    updateStatus: (userId: string | number, params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/mobile-users/status/${userId}`, params),
    details: (userId: string | number) => `/${DASH}/mobile-users/${userId}`,
  },
  admins: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/admin-users`, params),
    create: `/${DASH}/admin-users`,
    update: (userId: string | number) => `/${DASH}/admin-users/${userId}`,
    getById: (userId: string | number) => `/${DASH}/admin-users/${userId}`,
    details: (userId: string | number) => `/${DASH}/admin-users/${userId}`,
    updateStatus: (userId: string | number, activeStaus: boolean) =>
      `/${DASH}/admin-users/${userId}/status?active=${activeStaus}`,
  },
  roles: {
    list: `/${DASH}/roles`,
    create: `/${DASH}/roles/create`,
    update: (id: string) => `/${DASH}/roles/update/${id}`,
    details: (id: string) => `/${DASH}/roles/${id}`,
  },
  campaigns: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/campaigns`, params),
    create: `/${DASH}/campaigns`,
    details: (campaignId: string | number) =>
      `/${DASH}/campaigns/${campaignId}`,
    mediaFileUpload: `/${DASH}/acr/media`,
    mediaFileDelete: (fileId: string | number) =>
      `/${DASH}/acr/media/${fileId}`,
    update: (campaignId: string | number) =>
      `/${DASH}/campaigns/${campaignId}`,
    locationsByClient: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/campaigns/locations/by-client`, params),
  },
  survey: {
    list: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/surveys`, params),
    create: `/${DASH}/surveys`,
    update: (id: string) => `/${DASH}/surveys/${id}`,
    details: (id: string) => `/${DASH}/surveys/${id}`,
    saveBuckets: (surveyId: string) => `/${DASH}/surveys/${surveyId}/buckets`,
    calculateSurvey: (surveyId: string) =>
      `/${DASH}/surveys/${surveyId}/calculate`,
    campaignList: `/${DASH}/campaigns/all`,
    allSurveys: `/${DASH}/surveys/all`, // ID and Name only
    surveysByClientAndDateRange: (params?: QueryParams) =>
      filterParamsToQuery(`/${DASH}/surveys/by-client-and-date-range`, params),
    forstaPrograms: `/${DASH}/forsta/program-list`,
    forstaScenarios: (programId: string) =>
      `/${DASH}/forsta/scenario-list/${programId}`,
    forstaSurveys: (programId: string) =>
      `/${DASH}/forsta/survey-list/${programId}`,
    forstaSurveyDelete: (forstaId: string) =>
      `/${DASH}/forsta/delete-forsta-survey/${forstaId}`,
  },
  profile: {
    getMe: "/my-profile",
    updateMe: "/my-profile",
    updatePassword: "/my-profile/update-password",
  },
  settings: {
    general: "/settings/general",
    privacy: "/settings/privacy",
    notifications: "/settings/notifications",
  },
  notifications: {
    // list: "/notifications",
    create: "/notifications/send-to-topic",
    sendToTopic: (surveyId: string | number) => `/notifications/send-to-topic/${surveyId}`,
    // details: (id: string) => `/notifications/${id}`,
  },
  publicApis: {
    countryList: "/p/country-list",
    languageList: "/p/language-list",
  },
};

export default endpoints;
